import { ArrowLeft, Check, FileText, FlaskConical, Info, Loader2, Plus, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useDiscussoesControllerAdvisors,
  useDiscussoesControllerAdvisorsDisponiveis,
  useDiscussoesControllerAtualizarRascunho,
  useDiscussoesControllerCriarExameExterno,
  useDiscussoesControllerCriarRascunho,
  useDiscussoesControllerEnviarAnexo,
  useDiscussoesControllerListarEspecialidades,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { useExamesControllerListar } from '@/api/generated/exames/exames';
import type { AdvisorDisponivelResponse, PacienteResponse, RascunhoDiscussaoResponse } from '@/api/generated/model';
import { usePacientesControllerListar, usePacientesControllerObter } from '@/api/generated/pacientes/pacientes';
import { ApiError } from '@/api/http-client';
import { CORES_SEXO, ESPECIES, formatarIdade, SEXOS } from '@/features/pacientes/lib/especies';
import type { MensagemPendente } from '@/features/discussoes/tipos';
import { Avatar } from '@/shared/components/Avatar';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { cn } from '@/shared/lib/cn';
import { ROTULO_DIA } from '@/shared/lib/diasDaSemana';
import { ROTAS } from '@/shared/lib/rotas';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

/**
 * 8 passos — 2 a mais que o app mobile (`App/src/app/advisor/nova.tsx`), de
 * propósito: "Especialistas disponíveis" é o passo 0, puramente informativo
 * (transparência ANTES de qualquer escolha — a escolha em si continua só no
 * passo "Conexão com especialista"), e "Sua dúvida" é obrigatório e existe
 * porque escrever a dúvida própria estava enterrado como "outra dúvida"
 * opcional dentro do passo de dúvidas específicas — fácil de pular, mas é o
 * motivo real de abrir a discussão. Ordem: Especialistas disponíveis →
 * Paciente → Assunto (+ Especialidade) → Exames a compartilhar → Sua dúvida
 * → Dúvidas específicas → Conexão com especialista → Revisar e enviar.
 */
const ETAPAS = [
  'Especialistas disponíveis',
  'Paciente',
  'Assunto',
  'Exames a compartilhar',
  'Sua dúvida',
  'Dúvidas específicas',
  'Conexão com especialista',
  'Revisar e enviar',
] as const;

/** Mesmos grupos e perguntas do app — o usuário escolhe o grupo e marca as dúvidas dele. */
const DUVIDAS_POR_GRUPO: Record<string, string[]> = {
  Diagnóstico: [
    'Qual a principal hipótese diagnóstica neste momento?',
    'Quais diagnósticos diferenciais devo considerar?',
    'Os achados laboratoriais são compatíveis com o quadro clínico?',
  ],
  'Exames complementares': [
    'Quais exames complementares vocês recomendam?',
    'Vale a pena repetir algum exame já realizado?',
    'Há interferentes que possam estar afetando os resultados?',
  ],
  Tratamento: [
    'Qual o melhor protocolo terapêutico para este caso?',
    'Há risco de interação medicamentosa com o tratamento atual?',
    'Qual a dose recomendada considerando peso e espécie?',
  ],
  Prognóstico: [
    'Qual o prognóstico esperado para este paciente?',
    'Em quanto tempo esperar resposta ao tratamento?',
    'Quais sinais de alerta devo monitorar?',
  ],
};
const GRUPOS_DUVIDAS = Object.keys(DUVIDAS_POR_GRUPO);

const TEXTO_TERMO_DE_USO = `Ao utilizar esta plataforma, o médico-veterinário declara estar regularmente inscrito no CRMV e reconhece que as orientações disponibilizadas possuem caráter exclusivamente técnico e consultivo, destinadas ao apoio na interpretação de resultados laboratoriais e na discussão técnica de casos clínicos.

As orientações são elaboradas com base exclusivamente nas informações fornecidas pelo usuário, sem avaliação clínica direta do paciente, não constituindo diagnóstico, prescrição, tratamento ou determinação obrigatória de conduta profissional.

Compete exclusivamente ao médico-veterinário responsável pelo atendimento do animal a avaliação clínica do paciente, a interpretação dos exames em conjunto com os demais achados, a definição do diagnóstico, a prescrição do tratamento e a adoção das condutas profissionais cabíveis, assumindo a responsabilidade técnica, ética e legal por tais decisões.

A plataforma constitui ferramenta de apoio técnico e não substitui o exercício profissional do médico-veterinário assistente, permanecendo sob sua exclusiva responsabilidade as decisões clínicas adotadas no atendimento ao paciente.`;

interface NovoExame {
  exameId: string;
  nomeExame: string;
  nomeArquivo: string;
  arquivoId: string;
}

interface Especialidade {
  id: string;
  nome: string;
}

const TODAS = 'TODAS';
const TODOS = 'TODOS';

const LIMITE_BIO_ADVISOR = 200;

/** Bio do advisor cortada em `LIMITE_BIO_ADVISOR` caracteres — o "Ver perfil" (ⓘ) mostra o texto inteiro. */
function truncarBio(bio: string): string {
  const texto = bio.trim();
  if (texto.length <= LIMITE_BIO_ADVISOR) return texto;
  return `${texto.slice(0, LIMITE_BIO_ADVISOR)}...`;
}

interface Props {
  /**
   * Chamado com o id da discussão RASCUNHO já completa (o wizard só chega no
   * último passo depois de todo o resto ter sido salvo incrementalmente — ver
   * `criarRascunho`/`atualizarRascunho` abaixo) e as mensagens de
   * acompanhamento (anexos de exames novos) ainda não enviadas: a discussão
   * só aceita mensagem depois de sair de RASCUNHO. Quem chama guarda a lista
   * e manda depois.
   *
   * Pode ser assíncrono: quem chama tenta liquidar o rascunho com crédito
   * aqui. É aguardado para que uma falha lá vire o mesmo `erro` da tela que
   * uma falha de salvar passo — e não uma rejeição solta.
   */
  aoAbrir: (discussaoId: string, mensagensPendentes: MensagemPendente[]) => void | Promise<void>;
  /**
   * Presente ao RETOMAR um rascunho existente (a pessoa saiu no meio e voltou
   * pela listagem de discussões) — hidrata o wizard e pousa direto na etapa
   * salva, em vez de começar do zero. Ausente = rascunho novo.
   */
  rascunho?: RascunhoDiscussaoResponse;
}

export function AberturaDiscussaoWizard({ aoAbrir, rascunho }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState(rascunho?.ultimaEtapaWizard ?? 0);
  const [erro, setErro] = useState<string | null>(null);

  // O rascunho nasce no servidor na transição do passo 2 → 3 (ver
  // `aoAvancar`) — antes disso `discussaoId` é `null`. Ao RETOMAR, já chega
  // preenchido, e o passo 2→3 vira `atualizarRascunho` em vez de
  // `criarRascunho` (o rascunho já existe).
  const [discussaoId, setDiscussaoId] = useState<string | null>(rascunho?.id ?? null);

  // Passo 0 — paciente
  const [buscaPaciente, setBuscaPaciente] = useState('');
  const [filtroEspecie, setFiltroEspecie] = useState<string>(TODAS);
  const [filtroRaca, setFiltroRaca] = useState<string>(TODAS);
  const [filtroSexo, setFiltroSexo] = useState<string>(TODOS);
  const [pacienteSelecionado, setPacienteSelecionado] = useState<PacienteResponse | null>(null);
  const [detalhesAbertos, setDetalhesAbertos] = useState(Boolean(rascunho));

  // Passo 1 — assunto + especialidade
  const [especialidade, setEspecialidade] = useState<Especialidade | null>(null);
  const [assunto, setAssunto] = useState(rascunho?.assunto ?? '');

  // Passo 2 — exames a compartilhar
  const [compartilharTodos, setCompartilharTodos] = useState(!rascunho);
  const [novosExames, setNovosExames] = useState<NovoExame[]>([]);
  const [novoExameAberto, setNovoExameAberto] = useState(false);

  // Passo 3 — sua dúvida (obrigatório)
  const [duvidaPropria, setDuvidaPropria] = useState(rascunho?.duvidaPropria ?? '');

  // Passo 4 — dúvidas específicas (opcional)
  const [grupoAtivo, setGrupoAtivo] = useState<string>(GRUPOS_DUVIDAS[0] ?? '');
  const [duvidasSelecionadas, setDuvidasSelecionadas] = useState<Set<string>>(
    new Set(rascunho?.duvidasEspecificas ?? []),
  );

  // Passo 5 — conexão com especialista
  const [advisorId, setAdvisorId] = useState<string | null>(rascunho?.advisorId ?? null);
  const [advisorInfo, setAdvisorInfo] = useState<AdvisorDisponivelResponse | null>(null);

  // Passo 6 — termo de uso. Sempre reinicia em `false`, mesmo ao retomar: é
  // um aceite explícito, não um dado que sobrevive a sair e voltar.
  const [termoAberto, setTermoAberto] = useState(false);
  const [termoJaLido, setTermoJaLido] = useState(false);
  const [termoAceito, setTermoAceito] = useState(false);

  const { data: pacientesResp, isPending: pacientesCarregando } = usePacientesControllerListar({
    pagina: 1,
    porPagina: 100,
    ordenarPor: 'nome',
    ordem: 'asc',
  });
  const pacientes = useMemo(() => pacientesResp?.itens ?? [], [pacientesResp]);

  const { data: advisorsResp, isPending: advisorsCarregando } = useDiscussoesControllerAdvisorsDisponiveis();
  const advisors = useMemo(
    () => [...(advisorsResp?.itens ?? [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [advisorsResp],
  );

  const { data: todosAdvisorsResp, isPending: todosAdvisorsCarregando } = useDiscussoesControllerAdvisors();
  const todosAdvisors = useMemo(
    () => [...(todosAdvisorsResp?.itens ?? [])].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')),
    [todosAdvisorsResp],
  );

  const { data: examesResp } = useExamesControllerListar(
    { pacienteId: pacienteSelecionado?.id ?? '', pagina: 1, porPagina: 100, ordenarPor: 'criadoEm', ordem: 'desc' },
    { query: { enabled: Boolean(pacienteSelecionado) } },
  );
  const examesDoPaciente = examesResp?.itens ?? [];

  const criarRascunho = useDiscussoesControllerCriarRascunho();
  const atualizarRascunho = useDiscussoesControllerAtualizarRascunho();
  const salvando = criarRascunho.isPending || atualizarRascunho.isPending;

  // ── Hidratação ao retomar ────────────────────────────────────────────────
  // `rascunho` só traz ids (pacienteId, especialidadeId) — os objetos
  // completos, pra exibir nome/foto, vêm de uma busca própria: o paciente
  // pode estar fora dos 100 mais recentes de `pacientes` acima.
  const { data: pacienteDoRascunho } = usePacientesControllerObter(rascunho?.pacienteId ?? '', {
    query: { enabled: Boolean(rascunho?.pacienteId) && !pacienteSelecionado },
  });
  useEffect(() => {
    if (pacienteDoRascunho && !pacienteSelecionado) setPacienteSelecionado(pacienteDoRascunho);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacienteDoRascunho]);

  const { data: especialidadesResp } = useDiscussoesControllerListarEspecialidades(undefined, {
    query: { enabled: Boolean(rascunho?.especialidadeId) && !especialidade },
  });
  useEffect(() => {
    if (!rascunho?.especialidadeId || especialidade) return;
    const encontrada = especialidadesResp?.itens.find((e) => e.id === rascunho.especialidadeId);
    if (encontrada) setEspecialidade(encontrada);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [especialidadesResp]);

  // `exameIds` salvos batem com "compartilhar todos" quando cobrem TODO
  // exame que o paciente tem hoje — é a única leitura que o toggle atual
  // consegue representar (não existe seleção parcial de exame já cadastrado
  // nesta tela). Se o rascunho guardou só uma parte, o toggle nasce desligado
  // e a pessoa reseleciona — melhor que fingir uma seleção que já não é bem
  // aquela. Roda uma vez só (`hidratouExames`): depois disso é a pessoa quem
  // decide o toggle, não o efeito.
  const hidratouExames = useRef(false);
  useEffect(() => {
    if (!rascunho || hidratouExames.current || !examesResp) return;
    hidratouExames.current = true;
    const idsSalvos = new Set(rascunho.exameIds);
    setCompartilharTodos(examesDoPaciente.length > 0 && examesDoPaciente.every((e) => idsSalvos.has(e.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examesResp]);

  const especiesDisponiveis = useMemo(
    () => Array.from(new Set(pacientes.map((p) => p.especie))).sort(),
    [pacientes],
  );
  const racasDisponiveis = useMemo(
    () => Array.from(new Set(pacientes.map((p) => p.raca).filter((r): r is string => Boolean(r)))).sort(),
    [pacientes],
  );
  const sexosDisponiveis = useMemo(
    () => Array.from(new Set(pacientes.map((p) => p.sexo))).sort(),
    [pacientes],
  );

  const pacientesFiltrados = useMemo(() => {
    let lista = pacientes;
    if (filtroEspecie !== TODAS) lista = lista.filter((p) => p.especie === filtroEspecie);
    if (filtroRaca !== TODAS) lista = lista.filter((p) => p.raca === filtroRaca);
    if (filtroSexo !== TODOS) lista = lista.filter((p) => p.sexo === filtroSexo);

    const termo = buscaPaciente.trim().toLowerCase();
    if (!termo) {
      return [...lista]
        .sort((a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime())
        .slice(0, 5);
    }
    return lista.filter(
      (p) => p.nome.toLowerCase().includes(termo) || (p.tutorNome?.toLowerCase().includes(termo) ?? false),
    );
  }, [pacientes, filtroEspecie, filtroRaca, filtroSexo, buscaPaciente]);

  const advisorSelecionado = advisors.find((a) => a.id === advisorId) ?? null;
  const filtrosAtivos = filtroEspecie !== TODAS || filtroRaca !== TODAS || filtroSexo !== TODOS;

  function alternarDuvida(texto: string) {
    setDuvidasSelecionadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(texto)) proximo.delete(texto);
      else proximo.add(texto);
      return proximo;
    });
  }

  function validar(): string | null {
    if (step === 1 && !pacienteSelecionado) return 'Selecione um paciente para continuar.';
    if (step === 2 && assunto.trim().length < 3) return 'Descreva o assunto da discussão.';
    if (step === 4 && duvidaPropria.trim().length < 3) {
      return 'Escreva sua dúvida para o especialista.';
    }
    if (step === 6 && !advisorId) return 'Selecione um especialista.';
    if (step === 7 && !termoAceito) return 'Leia e aceite o Termo de Uso para enviar.';
    return null;
  }

  /**
   * Cada avanço de passo salva no servidor, não só localmente — ao contrário
   * do wizard antigo (que só falava com o backend no último passo), o
   * rascunho nasce cedo (passo 2 → 3) e evolui por `atualizarRascunho`
   * daqui pra frente. É o que dá visibilidade de abandono (`ultimaEtapaWizard`)
   * e o que deixa retomar de onde parou.
   */
  async function aoAvancar() {
    const mensagemErro = validar();
    if (mensagemErro) {
      setErro(mensagemErro);
      return;
    }
    setErro(null);

    try {
      // Primeiro ponto em que paciente + assunto (já validados acima)
      // existem juntos — nasce o rascunho aqui. Se já existir (a pessoa
      // voltou e editou paciente/assunto/especialidade), atualiza em vez de
      // criar de novo.
      if (step === 2) {
        if (!pacienteSelecionado) return;
        const dadosDoRascunho = {
          pacienteId: pacienteSelecionado.id,
          assunto: assunto.trim().slice(0, 300),
          ...(especialidade ? { especialidadeId: especialidade.id } : {}),
        };

        if (discussaoId) {
          await atualizarRascunho.mutateAsync({ id: discussaoId, data: { etapa: 3, ...dadosDoRascunho } });
        } else {
          const criado = await criarRascunho.mutateAsync({ data: dadosDoRascunho });
          setDiscussaoId(criado.id);
        }
        setStep(3);
        return;
      }

      // Passos 3 a 6: um PATCH por avanço, só com o campo que aquele passo é
      // dono — inclusive quando `advisorId` chega pela primeira vez (passo 6),
      // o servidor é quem cria o participante/atribuição dele.
      if (step >= 3 && step < ETAPAS.length - 1) {
        if (!discussaoId) return; // não deveria acontecer — o passo 2 já garante o rascunho.
        const proximaEtapa = step + 1;

        await atualizarRascunho.mutateAsync({
          id: discussaoId,
          data: {
            etapa: proximaEtapa,
            ...(step === 3
              ? {
                  exameIds: [
                    ...(compartilharTodos ? examesDoPaciente.map((e) => e.id) : []),
                    ...novosExames.map((e) => e.exameId),
                  ],
                }
              : {}),
            ...(step === 4 ? { duvidaPropria: duvidaPropria.trim() } : {}),
            ...(step === 5 ? { duvidasEspecificas: [...duvidasSelecionadas] } : {}),
            ...(step === 6 && advisorId ? { advisorId } : {}),
          },
        });
        setStep(proximaEtapa);
        return;
      }

      // "Revisar e enviar": tudo já foi salvo nos passos anteriores — só
      // falta despachar (checar crédito / ir pro pagamento, decidido por
      // quem chama via `aoAbrir`).
      if (step === ETAPAS.length - 1) {
        if (!discussaoId) return;
        // Anexo de exame novo é o único aviso que ainda depende do navegador
        // ficar aberto até aqui: o arquivo já está seguro no prontuário
        // (virou `Exame`, incluído no `exameIds` do passo 3), a única coisa
        // perdida seria este aviso no chat.
        const mensagensPendentes: MensagemPendente[] = novosExames.map((novo) => ({
          corpo: `📎 Exame anexado: ${novo.nomeExame} (${novo.nomeArquivo})`,
          clientMsgId: `${discussaoId}-anexo-${novo.exameId}`,
          arquivoId: novo.arquivoId,
        }));
        await aoAbrir(discussaoId, mensagensPendentes);
        return;
      }

      // Passos 0 (informativo) e 1 (paciente): nada pra salvar ainda.
      setStep((s) => s + 1);
    } catch (e) {
      if (e instanceof ApiError && e.codigo === 'ADVISOR_INVALIDO') {
        setStep(6);
        setErro(e.message);
        return;
      }
      setErro(e instanceof ApiError ? e.message : 'Não foi possível salvar. Tente novamente.');
    }
  }

  function aoVoltar() {
    setErro(null);
    if (step > 0) setStep((s) => s - 1);
    else navigate(ROTAS.discussoes);
  }

  const linhasResumo: [string, string | null][] = [
    ['Paciente', pacienteSelecionado?.nome ?? 'Nenhum vinculado'],
    ['Especialidade', especialidade?.nome ?? null],
    ['Assunto', assunto.trim() || null],
    [
      'Exames compartilhados',
      String((compartilharTodos ? examesDoPaciente.length : 0) + novosExames.length),
    ],
    ['Sua dúvida', duvidaPropria.trim() || null],
    ['Dúvidas específicas', duvidasSelecionadas.size > 0 ? String(duvidasSelecionadas.size) : null],
    ['Conexão com especialista', advisorSelecionado?.nome ?? null],
  ];

  return (
    <div className="space-y-5">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={aoVoltar}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Voltar"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <p className="text-sm text-muted-foreground">
            Passo {step + 1} de {ETAPAS.length} · {ETAPAS[step]}
          </p>
        </div>
        <div className="flex gap-1.5">
          {ETAPAS.map((etapa, i) => (
            <div key={etapa} className={cn('h-1 flex-1 rounded-full', i <= step ? 'bg-brand' : 'bg-muted')} />
          ))}
        </div>
      </div>

      {step === 0 && (
        <div className="space-y-4">
          <div className="flex items-start gap-2.5 rounded-lg border border-brand/20 bg-brand/5 p-3">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-brand" />
            <p className="text-sm font-medium text-foreground">
              Conheça os especialistas disponíveis. Você poderá escolher com quem deseja discutir o
              seu caso nas próximas etapas.
            </p>
          </div>

          {todosAdvisorsCarregando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Carregando especialistas...</p>
          ) : todosAdvisors.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center">
              <Sparkles className="w-6 h-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Nenhum especialista cadastrado no momento.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {todosAdvisors.map((a) => (
                <div
                  key={a.id}
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-2 border-brand/20 bg-surface-raised p-3.5',
                    !a.disponivelHoje && 'opacity-50',
                  )}
                >
                  <Avatar
                    fotoUrl={a.fotoUrl}
                    nome={a.nome}
                    variante="suave"
                    iniciais={2}
                    className="w-11 h-11 text-sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{a.nome}</p>
                    {a.especialidade && <p className="text-xs text-muted-foreground">{a.especialidade}</p>}
                    <p className={cn('text-xs mt-0.5', a.disponivelHoje ? 'text-emerald-600' : 'text-muted-foreground')}>
                      {a.disponivelHoje
                        ? 'Disponível hoje'
                        : a.proximoDiaDisponivel
                          ? `A partir de ${ROTULO_DIA[a.proximoDiaDisponivel]}`
                          : 'Sem dias configurados'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Selecione o paciente sobre o qual a discussão vai ser aberta.
          </p>

          <Input
            value={buscaPaciente}
            onChange={(e) => setBuscaPaciente(e.target.value)}
            placeholder="Buscar paciente por nome ou responsável"
            className={CLASSE_CONTROLE}
          />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={filtroEspecie} onValueChange={setFiltroEspecie}>
              <SelectTrigger className={CLASSE_CONTROLE} aria-label="Filtrar por espécie">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as espécies</SelectItem>
                {especiesDisponiveis.map((e) => (
                  <SelectItem key={e} value={e}>
                    {ESPECIES[e]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroRaca} onValueChange={setFiltroRaca}>
              <SelectTrigger className={CLASSE_CONTROLE} aria-label="Filtrar por raça">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODAS}>Todas as raças</SelectItem>
                {racasDisponiveis.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filtroSexo} onValueChange={setFiltroSexo}>
              <SelectTrigger className={CLASSE_CONTROLE} aria-label="Filtrar por sexo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os sexos</SelectItem>
                {sexosDisponiveis.map((s) => (
                  <SelectItem key={s} value={s}>
                    {SEXOS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!buscaPaciente.trim() && (
            <p className="text-xs text-muted-foreground">
              Mostrando os 5 pacientes mais recentes{filtrosAtivos ? ' dentro do filtro' : ''}. Não achou? Use a
              busca acima.
            </p>
          )}

          {pacientesCarregando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Carregando pacientes...</p>
          ) : pacientesFiltrados.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Nenhum paciente encontrado.</p>
          ) : (
            <div className="space-y-2">
              {pacientesFiltrados.map((p) => {
                const selecionado = pacienteSelecionado?.id === p.id;
                const cores = CORES_SEXO[p.sexo];
                return (
                  <div
                    key={p.id}
                    className={cn(
                      'rounded-lg border-2 overflow-hidden',
                      selecionado ? cores.bordaSelecionada : cores.borda,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        if (selecionado) {
                          setDetalhesAbertos((v) => !v);
                        } else {
                          setPacienteSelecionado(p);
                          setCompartilharTodos(true);
                          setNovosExames([]);
                          setDetalhesAbertos(true);
                        }
                      }}
                      className={cn(
                        'w-full flex items-center gap-3 p-3 text-left',
                        selecionado ? cores.fundo : 'bg-surface-raised hover:bg-muted/40',
                      )}
                    >
                      <div
                        className={cn(
                          'w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold uppercase',
                          cores.fundo,
                          cores.texto,
                        )}
                      >
                        {p.nome.slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{p.nome}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {[ESPECIES[p.especie], p.raca].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                    </button>
                    {selecionado && detalhesAbertos && (
                      <div className="border-t border-brand/10 bg-surface-raised px-4 py-2 text-sm">
                        {(
                          [
                            ['Espécie', ESPECIES[p.especie]],
                            ['Raça', p.raca],
                            ['Idade', formatarIdade(p.idadeMeses)],
                            ['Sexo', SEXOS[p.sexo]],
                            ['Responsável', p.tutorNome],
                          ] as [string, string | null][]
                        )
                          .filter(([, v]) => Boolean(v))
                          .map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between py-1">
                              <span className="text-xs text-muted-foreground">{k}</span>
                              <span className="text-xs font-medium text-foreground">{v}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <EspecialidadePicker value={especialidade} onChange={setEspecialidade} />

          <Campo id="assunto" rotulo="Assunto da discussão" obrigatorio>
            <Textarea
              id="assunto"
              rows={4}
              maxLength={300}
              placeholder="Ex.: interpretação de painel hormonal em cão com suspeita de hipotireoidismo"
              className={CLASSE_CONTROLE}
              value={assunto}
              onChange={(e) => setAssunto(e.target.value)}
            />
          </Campo>
          <p className="text-right text-xs text-muted-foreground -mt-3">{assunto.length}/300</p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          {examesDoPaciente.length > 0 ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-brand/20 bg-surface-raised p-4">
              <div>
                <p className="text-sm font-semibold text-foreground">
                  Compartilhar todos os exames já cadastrados
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {examesDoPaciente.length} exame(s) de {pacienteSelecionado?.nome} ficam visíveis (somente leitura)
                  para o especialista.
                </p>
              </div>
              <Switch checked={compartilharTodos} onCheckedChange={setCompartilharTodos} />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <FlaskConical className="w-6 h-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {pacienteSelecionado?.nome} ainda não tem exames cadastrados na plataforma.
              </p>
            </div>
          )}

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-2">Exame não cadastrado na plataforma</p>
            <Button type="button" variant="outline" onClick={() => setNovoExameAberto(true)}>
              <Plus className="w-4 h-4" />
              Adicionar exame
            </Button>
            <p className="mt-1.5 text-xs text-muted-foreground">
              Sobe o arquivo do dispositivo e cadastra o exame no prontuário do paciente.
            </p>
          </div>

          {novosExames.length > 0 && (
            <div className="space-y-2">
              {novosExames.map((e, i) => (
                <div
                  key={e.exameId}
                  className="flex items-center gap-3 rounded-lg border border-brand/20 bg-surface-raised p-3"
                >
                  <div className="w-9 h-9 shrink-0 rounded-lg bg-brand/15 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-brand" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{e.nomeExame}</p>
                    <p className="text-xs text-muted-foreground truncate">{e.nomeArquivo}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setNovosExames((atual) => atual.filter((_, j) => j !== i))}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Remover exame"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {pacienteSelecionado && (
            <NovoExameDialog
              aberto={novoExameAberto}
              pacienteId={pacienteSelecionado.id}
              aoFechar={() => setNovoExameAberto(false)}
              aoAdicionar={(novo) => setNovosExames((atual) => [...atual, novo])}
            />
          )}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-5">
          <p className="text-sm text-muted-foreground">
            Escreva com suas palavras a dúvida que motivou abrir esta discussão — é o que o especialista vai ler
            primeiro.
          </p>

          <Campo id="duvidaPropria" rotulo="Sua dúvida" obrigatorio>
            <Textarea
              id="duvidaPropria"
              rows={6}
              maxLength={1000}
              placeholder="Ex.: cão com hipotireoidismo suspeito, T4 baixo e TSH normal — como interpretar?"
              className={CLASSE_CONTROLE}
              value={duvidaPropria}
              onChange={(e) => setDuvidaPropria(e.target.value)}
            />
          </Campo>
          <p className="text-right text-xs text-muted-foreground -mt-3">{duvidaPropria.length}/1000</p>
        </div>
      )}

      {step === 5 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Escolha um grupo e selecione as dúvidas para orientar o especialista na discussão (opcional).
          </p>

          <div className="flex flex-wrap gap-2">
            {GRUPOS_DUVIDAS.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGrupoAtivo(g)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-xs font-medium',
                  grupoAtivo === g
                    ? 'border-brand bg-brand text-brand-foreground'
                    : 'border-brand/30 text-muted-foreground hover:bg-muted/40',
                )}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="space-y-2">
            {(DUVIDAS_POR_GRUPO[grupoAtivo] ?? []).map((d) => {
              const selecionada = duvidasSelecionadas.has(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => alternarDuvida(d)}
                  className={cn(
                    'w-full flex items-start gap-2.5 rounded-lg border-2 p-3 text-left',
                    selecionada ? 'border-emerald-500 bg-emerald-500/10' : 'border-brand/20 bg-surface-raised',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2',
                      selecionada ? 'border-emerald-500 bg-emerald-500' : 'border-brand/30',
                    )}
                  >
                    {selecionada && <Check className="w-3 h-3 text-white" />}
                  </span>
                  <span className="text-sm text-foreground">{d}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 6 && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Escolha 1 especialista com vaga disponível. Clique no ⓘ para ver o perfil.
          </p>

          {advisorsCarregando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Carregando especialistas...</p>
          ) : advisors.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-14 text-center">
              <Sparkles className="w-6 h-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Nenhum especialista com vaga no momento. Tente novamente mais tarde.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {advisors.map((a) => {
                const selecionado = advisorId === a.id;
                return (
                  <div
                    key={a.id}
                    className={cn(
                      'rounded-lg border-2 p-3.5',
                      selecionado ? 'border-brand bg-brand/10' : 'border-brand/20 bg-surface-raised',
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        fotoUrl={a.fotoUrl}
                        nome={a.nome}
                        variante="suave"
                        iniciais={2}
                        className="w-11 h-11 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => setAdvisorId((cur) => (cur === a.id ? null : a.id))}
                        className="flex-1 text-left"
                      >
                        <p className="text-sm font-semibold text-foreground">{a.nome}</p>
                        {a.especialidade && <p className="text-xs text-muted-foreground">{a.especialidade}</p>}
                        {selecionado && a.bio && (
                          <p className="text-xs text-foreground mt-1.5">{truncarBio(a.bio)}</p>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAdvisorId((cur) => (cur === a.id ? null : a.id))}
                        className={cn(
                          'h-5 w-5 shrink-0 rounded-full border-2',
                          selecionado ? 'border-brand bg-brand' : 'border-brand/30',
                        )}
                        aria-label={selecionado ? 'Desmarcar' : 'Selecionar'}
                      />
                      <button
                        type="button"
                        onClick={() => setAdvisorInfo(a)}
                        className="text-brand"
                        aria-label="Ver perfil"
                      >
                        <Info className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Dialog open={Boolean(advisorInfo)} onOpenChange={(v) => !v && setAdvisorInfo(null)}>
            <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
              {advisorInfo && (
                <>
                  <DialogHeader>
                    <DialogTitle>Perfil do especialista</DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <Avatar
                      fotoUrl={advisorInfo.fotoUrl}
                      nome={advisorInfo.nome}
                      variante="suave"
                      iniciais={2}
                      className="w-16 h-16 text-lg"
                    />
                    <p className="text-base font-semibold text-foreground">{advisorInfo.nome}</p>
                    {advisorInfo.especialidade && (
                      <p className="text-sm text-muted-foreground">{advisorInfo.especialidade}</p>
                    )}
                    {advisorInfo.ufCrmv && (
                      <span className="rounded-full bg-brand/10 text-brand px-2.5 py-0.5 text-xs font-medium">
                        {advisorInfo.ufCrmv}
                      </span>
                    )}
                  </div>
                  {advisorInfo.bio && (
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                        Sobre
                      </p>
                      <p className="text-sm text-foreground whitespace-pre-wrap break-words">{advisorInfo.bio}</p>
                    </div>
                  )}
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>
      )}

      {step === 7 && (
        <div className="space-y-5">
          <div className="flex flex-col items-center rounded-lg border border-brand/20 bg-surface-raised p-6 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-lg font-semibold text-foreground">Pronto para enviar</p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Revise as informações antes de enviar a solicitação de discussão ao especialista selecionado.
            </p>
          </div>

          <div className="space-y-1">
            {linhasResumo
              .filter(([, v]) => Boolean(v))
              .map(([k, v]) => (
                <div key={k} className="flex items-start justify-between gap-3 px-0.5 py-1">
                  <span className="text-sm text-muted-foreground">{k}</span>
                  <span className="text-sm font-medium text-foreground text-right max-w-[60%]">{v}</span>
                </div>
              ))}
          </div>

          <div className="rounded-lg border border-brand/20 bg-muted/30 p-3.5">
            <p className="text-xs leading-5 text-muted-foreground">
              A Nohra promove discussão técnico-científica entre médicos-veterinários — a opinião do especialista
              associado não substitui atendimento presencial nem configura consulta ao responsável, e a responsabilidade
              pela condução do paciente permanece integralmente com o veterinário assistente.
            </p>
          </div>

          <Button type="button" variant="outline" className="w-full" onClick={() => setTermoAberto(true)}>
            <FileText className="w-4 h-4" />
            Ler Termo de Uso
          </Button>

          <button
            type="button"
            disabled={!termoJaLido}
            onClick={() => termoJaLido && setTermoAceito((v) => !v)}
            className={cn(
              'w-full flex items-center gap-2.5 rounded-lg border-2 p-3.5 text-left',
              termoAceito ? 'border-emerald-500 bg-emerald-500/10' : 'border-brand/20 bg-surface-raised',
              !termoJaLido && 'opacity-50 cursor-not-allowed',
            )}
          >
            <span
              className={cn(
                'flex h-5 w-5 shrink-0 items-center justify-center rounded border-2',
                termoAceito ? 'border-emerald-500 bg-emerald-500' : 'border-brand/30',
              )}
            >
              {termoAceito && <Check className="w-3 h-3 text-white" />}
            </span>
            <span className="text-sm text-foreground">
              {termoJaLido ? 'Eu concordo com o Termo de Uso' : 'Leia o Termo de Uso para poder aceitar'}
            </span>
          </button>

          <Dialog
            open={termoAberto}
            onOpenChange={(v) => {
              setTermoAberto(v);
              if (!v) setTermoJaLido(true);
            }}
          >
            <DialogContent className="max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Termo de Uso</DialogTitle>
              </DialogHeader>
              <p className="text-sm leading-6 text-foreground whitespace-pre-wrap">{TEXTO_TERMO_DE_USO}</p>
              <DialogFooter>
                <Button
                  type="button"
                  onClick={() => {
                    setTermoAberto(false);
                    setTermoJaLido(true);
                  }}
                >
                  Fechar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {erro && (
        <Alert variant="destructive">
          <AlertDescription>{erro}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={aoVoltar}>
          Voltar
        </Button>
        <Button
          type="button"
          disabled={salvando || (step === ETAPAS.length - 1 && !termoAceito)}
          onClick={() => void aoAvancar()}
        >
          {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {step === ETAPAS.length - 1 ? 'Enviar solicitação de discussão' : 'Continuar'}
        </Button>
      </div>
    </div>
  );
}

/**
 * Só ESCOLHE entre as especialidades já cadastradas — cadastrar uma nova é
 * ação de admin, feita fora do wizard em Administração → Especialidades de
 * discussão técnica (`admin_especialidades_discussao.criar`).
 */
function EspecialidadePicker({
  value,
  onChange,
}: {
  value: Especialidade | null;
  onChange: (v: Especialidade | null) => void;
}) {
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(false);
  const { data, isFetching } = useDiscussoesControllerListarEspecialidades(
    busca.trim() ? { busca: busca.trim() } : undefined,
    { query: { enabled: aberto } },
  );

  const resultados = data?.itens ?? [];

  if (value) {
    return (
      <div>
        <p className="text-sm font-medium text-brand mb-1.5">Especialidade da discussão</p>
        <div className="flex items-center justify-between rounded-lg border border-brand/30 bg-surface-raised px-4 py-2.5">
          <span className="text-sm font-medium text-foreground">{value.nome}</span>
          <button type="button" className="text-sm font-semibold text-brand" onClick={() => onChange(null)}>
            Trocar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Campo id="especialidade" rotulo="Especialidade da discussão" dica="Opcional — ajuda o advisor a se preparar.">
        <Input
          id="especialidade"
          value={busca}
          onFocus={() => setAberto(true)}
          onChange={(e) => {
            setBusca(e.target.value);
            setAberto(true);
          }}
          onBlur={() => setAberto(false)}
          placeholder="Clique para ver as especialidades cadastradas"
          className={CLASSE_CONTROLE}
        />
      </Campo>

      {aberto && (
        // onMouseDown com preventDefault: sem isso, o clique numa opção
        // dispara blur no input ANTES do onClick da opção, fechando a lista
        // e cancelando a seleção — clássico bug de combobox.
        <div
          onMouseDown={(e) => e.preventDefault()}
          className="mt-1.5 rounded-lg border border-brand/20 divide-y divide-brand/10 bg-surface-raised max-h-64 overflow-y-auto"
        >
          {isFetching ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : resultados.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {busca.trim() ? 'Nenhuma especialidade encontrada.' : 'Nenhuma especialidade cadastrada ainda.'}
            </p>
          ) : (
            resultados.map((e) => (
              <button
                key={e.id}
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm text-foreground hover:bg-muted/40"
                onClick={() => {
                  onChange({ id: e.id, nome: e.nome });
                  setBusca('');
                  setAberto(false);
                }}
              >
                {e.nome}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function NovoExameDialog({
  aberto,
  pacienteId,
  aoFechar,
  aoAdicionar,
}: {
  aberto: boolean;
  pacienteId: string;
  aoFechar: () => void;
  aoAdicionar: (novo: NovoExame) => void;
}) {
  const enviarAnexo = useDiscussoesControllerEnviarAnexo();
  const criarExameExterno = useDiscussoesControllerCriarExameExterno();

  const [dataRealizacao, setDataRealizacao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const hoje = new Date().toISOString().slice(0, 10);
  const enviando = enviarAnexo.isPending || criarExameExterno.isPending;

  async function confirmar() {
    setErro(null);
    if (!dataRealizacao) {
      setErro('Informe a data de realização.');
      return;
    }
    if (dataRealizacao > hoje) {
      setErro('A data de realização não pode ser no futuro.');
      return;
    }
    if (!arquivo) {
      setErro('Selecione o arquivo do exame.');
      return;
    }

    try {
      const anexo = await enviarAnexo.mutateAsync({ data: { arquivo } });
      const exame = await criarExameExterno.mutateAsync({
        data: {
          pacienteId,
          coletadoEm: new Date(`${dataRealizacao}T00:00:00.000Z`).toISOString(),
          arquivoId: anexo.arquivoId,
        },
      });
      aoAdicionar({ exameId: exame.id, nomeExame: exame.nome, nomeArquivo: arquivo.name, arquivoId: anexo.arquivoId });
      setDataRealizacao('');
      setArquivo(null);
      aoFechar();
    } catch {
      setErro('Não foi possível adicionar o exame. Tente novamente.');
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adicionar exame</DialogTitle>
          <DialogDescription>
            Selecione o arquivo (PDF ou imagem) e informe a data em que o exame foi realizado. Fica registrado no
            prontuário como &quot;Documento externo&quot;.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Campo id="dataRealizacao" rotulo="Data de realização" obrigatorio>
            <Input
              id="dataRealizacao"
              type="date"
              max={hoje}
              value={dataRealizacao}
              onChange={(e) => setDataRealizacao(e.target.value)}
              className={CLASSE_CONTROLE}
            />
          </Campo>

          <label className="flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-brand/30 py-6 cursor-pointer hover:border-brand/50">
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            />
            <FileText className="w-5 h-5 text-brand" />
            <span className="text-sm font-medium text-brand">
              {arquivo ? arquivo.name : 'Clique para selecionar o arquivo'}
            </span>
          </label>

          {erro && <p className="text-xs text-destructive">{erro}</p>}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="button" disabled={enviando} onClick={() => void confirmar()}>
            {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
